import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { AddressType } from '@sg/garnish';
import { switchcase } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { getDayDateTimeYear, getOrderTimeRange, ignoreTimezone } from '@order/utils';

import { useDayLabel } from '../../../../hooks/useDayLabel';
import { getOrderTime } from '../../../../utils';

// ────────────────────────────────────────────────────────────────────────────────

export const useOrderStatusDetails = (params: UseOrderStatusDetailsParams) => {
  const {
    orderStatus,
    orderType,
    isPast,
    isTrackable,
    wantedTime,
    estimatedDeliveryTime = '',
    locationName: name,
    locationAddress: address,
  } = params;

  const { startTime, endTime, timeEstimate } = useMemo(() => {
    const orderTime = getOrderTime({
      wantedTime,
      deliveryOrderDetail: { estimatedDeliveryTime },
    });
    const estimate = orderTime ? ignoreTimezone(orderTime) : undefined;

    return { ...getOrderTimeRange(estimate), timeEstimate: estimate };
  }, [wantedTime, estimatedDeliveryTime]);

  const isFailed = orderStatus === FlattenedOrderStatuses.Failed;

  const { formatMessage } = useIntl();

  const day = useDayLabel(timeEstimate);
  const {
    date = '',
    time = '',
    year = '',
  } = timeEstimate ? getDayDateTimeYear(formatMessage, timeEstimate) : {};

  const localizationValues = {
    name,
    address,
    day,
    date,
    time,
    start_time: startTime,
    end_time: endTime,
    year,
    order_type: orderType,
  };

  if (orderType === 'in-store') {
    return formatMessage(messages.subtitlePickupCompleted, localizationValues);
  }

  if (isFailed) {
    return formatMessage(messages.subtitleFailed, localizationValues);
  }

  if (isPast) {
    return formatMessage(messages.subtitlePast, localizationValues);
  }

  if (!isTrackable) {
    return formatMessage(messages.subtitleUnavailable, localizationValues);
  }

  if (orderType === 'pickup') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitlePickupReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        messages.subtitlePickupPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitlePickupCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  if (orderType === 'outpost') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitleOutpostReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        messages.subtitleOutpostDelivering,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitleOutpostCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  if (orderType === 'delivery') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitleDeliveryReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        messages.subtitleDeliveryPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.ReadyForCourier]: formatMessage(
        messages.subtitleDeliveryCourier,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        messages.subtitleDeliveryRoute,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitleDeliveryCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  return formatMessage(messages.subtitleUnavailable, localizationValues);
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export type UseOrderStatusDetailsParams = {
  wantedTime?: string;
  estimatedDeliveryTime?: string;
  locationAddress?: string;
  locationName?: string;
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  isTrackable: boolean;
  isPast: boolean;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  subtitlePickupCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed pickup',
  },
  subtitleFailed: {
    defaultMessage:
      "We're so sorry we had to cancel your order, there was a snag with it. We are processing a full refund, which will post in your account within 3-5 business days.",
    description: 'Order Status | Info | Subtitle for failed order',
  },
  subtitlePast: {
    defaultMessage: 'Your order was completed {day}, {date} at {time}',
    description: 'Order Status | Info | Subtitle for past order',
  },
  subtitleUnavailable: {
    defaultMessage:
      '{order_type, select, pickup {Your pickup order is awaiting preparation.} outpost {Your outpost order is awaiting delivery.} delivery {Your delivery order is being prepared.} other {}}',
    description: 'Order Status | Info | Subtitle for unavailable order',
  },
  subtitlePickupReceived: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status | Info | Subtitle for received pickup order',
  },
  subtitlePickupPreparing: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status | Info | Subtitle for preparing pickup order',
  },
  subtitleOutpostReceived: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for received outpost order',
  },
  subtitleOutpostDelivering: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for delivering outpost order',
  },
  subtitleOutpostCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed outpost order',
  },
  subtitleDeliveryReceived: {
    defaultMessage: 'Scheduled delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for received delivery order',
  },
  subtitleDeliveryPreparing: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for preparing delivery order',
  },
  subtitleDeliveryCourier: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for ready for courier delivery order',
  },
  subtitleDeliveryRoute: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for delivering delivery order',
  },
  subtitleDeliveryCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed delivery order',
  },
});
